/**
 * ObjectiveDetailCard.tsx
 *
 * Renders the `objective_detail` envelope data from onething_manage_objective (get).
 * Embeds key-result rows using the shared KeyResultRow from KeyResultListCard.
 */

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { OkrStatusBadge } from "@/components/okr/OkrStatusBadge";
import { ObjectiveStatus } from "@/types";
import { KeyResultRow, progressFillClass, type KeyResultListRow } from "./KeyResultListCard";

// ---------------------------------------------------------------------------
// Data shape for kind "objective_detail"
// ---------------------------------------------------------------------------

export interface ObjectiveDetail {
  pid: string;
  code: string;
  name: string;
  status: string;
  progress: number;
  targetProgress: number;
  startDate?: string;
  dueDate?: string;
  description?: string;
  keyResults: KeyResultListRow[];
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface ObjectiveDetailCardProps {
  data: ObjectiveDetail;
}

export function ObjectiveDetailCard({ data }: ObjectiveDetailCardProps) {
  const pct =
    data.targetProgress > 0 ? Math.round((data.progress / data.targetProgress) * 100) : 0;

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      {/* Blue top accent — full-width 3px pill */}
      <div className="h-[3px] w-full bg-blue-500" />

      <CardHeader className="px-4 pt-3.5 pb-2">
        {/* Code + status */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">
            {data.code}
          </span>
          <OkrStatusBadge status={data.status as ObjectiveStatus} size="sm" className="rounded-full" />
        </div>
        {/* Name */}
        <p className="text-[15px] font-bold leading-snug tracking-tight">{data.name}</p>
        {/* Description */}
        {data.description && (
          <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">
            {data.description}
          </p>
        )}
        {/* Dates */}
        {(data.startDate || data.dueDate) && (
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {[data.startDate, data.dueDate].filter(Boolean).join(" → ")}
          </p>
        )}
        {/* Progress bar */}
        <div className="flex items-center gap-2 mt-2.5">
          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden shadow-inner">
            <div
              className={`h-full rounded-full ${progressFillClass(data.status)}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <span className="text-[10.5px] font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
            {data.progress}/{data.targetProgress} · {pct}%
          </span>
        </div>
      </CardHeader>

      {data.keyResults.length > 0 && (
        <>
          <div className="flex items-center justify-between border-t border-border/50 px-4 py-2">
            <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
              Key Results
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
              {data.keyResults.length}
            </span>
          </div>
          <CardContent className="px-2 pb-2 pt-0">
            {data.keyResults.map((kr) => (
              <KeyResultRow key={kr.code} row={kr} />
            ))}
          </CardContent>
        </>
      )}
    </Card>
  );
}
